import { supabaseClient } from '@/services/supabase';
import type { Router, RouteLocationRaw } from 'vue-router';
import type { AuthUser, UserRole } from '../types/auth';
import type { UserRow } from '../types/auth';
import { FrontendError, maybeThrowError } from '@/utils/error-helpers';
import { EdgeFunctionsManager } from '@/utils/edge-functions-manager';
import { isNonNullable, isNullable } from '@/utils/nullability-helpers';
import posthog from 'posthog-js';
import { createErrorHandler } from './error-handler';
import { errorConfigs } from '@/config/errors';
import { useAuthStore } from '@/stores/auth';
import camelcaseKeys from 'camelcase-keys';
import type { OrganizationInviteDetails } from '@shared-types/organizations';
import type { OtpChannel } from '@shared-types/otp';

export async function fetchUser(): Promise<AuthUser | undefined> {
    const { data } = await supabaseClient.auth.getUser();
    return data?.user as AuthUser | undefined;
}

// Shared by every sign-in path (password, phone OTP) so role resolution and posthog identify
// stay in one place instead of drifting between them.
function resolveRoleAfterSignIn(user: AuthUser, options?: { allowNoOrganization?: boolean }): UserRole | undefined {
    const { id, email, app_metadata } = user;
    if (isNullable(app_metadata.organizations)) {
        if (options?.allowNoOrganization) {
            return undefined;
        }
        throw new FrontendError('errors.frontend.userNotInOrganization');
    }
    const organizationId = Object.keys(app_metadata.organizations)[0];
    const role = app_metadata.organizations[organizationId][0];
    if (posthog.__loaded) {
        posthog.identify(id, { email, role });
    }
    return role;
}

export async function signInWithPassword(
    email: string,
    password: string,
    options?: { allowNoOrganization?: boolean },
): Promise<UserRole | undefined> {
    const { data, error } = await supabaseClient.auth.signInWithPassword({
        email,
        password,
    });
    if (error) {
        const handleUpdatePasswordError = createErrorHandler(errorConfigs.signIn);
        const key = handleUpdatePasswordError(error);
        maybeThrowError(error, key);
    }
    if (isNonNullable(data.user)) {
        return resolveRoleAfterSignIn(data.user as AuthUser, options);
    }
}

// Goes through an edge function, not signInWithOtp directly - only the service role can write the
// channel choice to otp_delivery_context for auth-send-sms to pick up.
export async function signInWithPhoneOtp(phone: string, channel: OtpChannel): Promise<void> {
    await EdgeFunctionsManager.requestPhoneLoginOtp(phone, channel);
}

export async function verifyPhoneLoginOtp(
    phone: string,
    token: string,
    options?: { allowNoOrganization?: boolean },
): Promise<UserRole | undefined> {
    const { data, error } = await supabaseClient.auth.verifyOtp({ phone, token, type: 'sms' });
    if (error) {
        const handleOtpError = createErrorHandler(errorConfigs.otp);
        const key = handleOtpError(error);
        maybeThrowError(error, key);
    }
    if (isNonNullable(data.user)) {
        return resolveRoleAfterSignIn(data.user as AuthUser, options);
    }
}

export async function signUpWithPassword(
    email: string,
    password: string,
    redirectUrl?: string,
    organizationInviteId?: string,
) {
    const { data, error } = await supabaseClient.auth.signUp({
        email,
        password,
        options: {
            emailRedirectTo: redirectUrl,
            // Stashed in user_metadata so the auth-send-email hook can resolve the confirmation
            // email's language from the inviting org's default (every signup is invite-originated).
            data: isNonNullable(organizationInviteId) ? { organization_invite_id: organizationInviteId } : undefined,
        },
    });
    switch (error?.code) {
        case 'user_already_exists':
            throw new FrontendError('errors.frontend.userAlreadyExists');
    }
    maybeThrowError(error, 'errors.frontend.signUpFailed');
    return data.user;
}

/** The email address an org invite is addressed to, used to lock the signup email field. */
export async function getInvitedEmail(inviteId: string): Promise<string | undefined> {
    const { data, error } = await supabaseClient.rpc('get_organization_invite_details', { invite_id: inviteId });
    maybeThrowError(error, 'errors.frontend.inviteNotFound');
    return (data as OrganizationInviteDetails | null)?.invitedEmail;
}

export async function sendPasswordResetEmail(email: string): Promise<void> {
    // Pass the user's current org (if any) so the edge side can resolve the email language via the
    // org default when the account has no preference yet. Undefined from the signed-out
    // forgot-password page — that path correctly falls back to English.
    const { organizationId } = useAuthStore();
    await EdgeFunctionsManager.sendPasswordResetEmail(email, organizationId.value ?? undefined);
}

export async function updateEmail(newEmail: string): Promise<void> {
    const oldEmail = (await fetchUser())?.email;
    if (isNullable(oldEmail)) {
        throw new FrontendError('errors.frontend.currentEmailFetchFailed');
    }

    // A phone-auth user with no email yet has oldEmail === '' (GoTrue's convention). Redirects to
    // /set-password, not /confirm-email-change, to force setting a password right away.
    // if (oldEmail === '') {
    //     const { error } = await supabaseClient.auth.updateUser(
    //         { email: newEmail },
    //         { emailRedirectTo: `${window.location.origin}/set-password` },
    //     );
    //     if (error) {
    //         const handleAddEmailError = createErrorHandler(errorConfigs.general);
    //         const key = handleAddEmailError(error);
    //         maybeThrowError(error, key);
    //     }
    //     return;
    // }

    const { organizationId } = useAuthStore();
    await EdgeFunctionsManager.changeEmail(oldEmail, newEmail, organizationId.value ?? undefined);
}

export async function updatePassword(password: string): Promise<void> {
    const { error } = await supabaseClient.auth.updateUser({ password });
    if (error) {
        const handleUpdatePasswordError = createErrorHandler(errorConfigs.updatePassword);
        const key = handleUpdatePasswordError(error);
        maybeThrowError(error, key);
    }
}

// Two-step, mirroring GoTrue's native email-change flow but for phone: updateUser sends an OTP to
// the new number, verifyOtp (type: 'phone_change') confirms it and swaps auth.users.phone over.
// No edge function needed — both calls go straight to GoTrue, same as the initial phone
// signup/login flow, so they reuse whatever SMS provider is already configured for that.
export async function requestPhoneChange(newPhone: string): Promise<void> {
    const { error } = await supabaseClient.auth.updateUser({ phone: newPhone });
    if (error) {
        const handlePhoneChangeError = createErrorHandler(errorConfigs.otp);
        const key = handlePhoneChangeError(error);
        maybeThrowError(error, key);
    }
}

export async function verifyPhoneChange(newPhone: string, token: string): Promise<void> {
    const { error } = await supabaseClient.auth.verifyOtp({ phone: newPhone, token, type: 'phone_change' });
    if (error) {
        const handlePhoneChangeError = createErrorHandler(errorConfigs.otp);
        const key = handlePhoneChangeError(error);
        maybeThrowError(error, key);
    }
}

export async function signOut(): Promise<void> {
    const { error } = await supabaseClient.auth.signOut();
    maybeThrowError(error, 'errors.frontend.signOutFailed');
}

// Shared by every place a session ends (explicit sign-out, idle timeout, session expiry) so an
// org's logoutRedirectUrl (e.g. bouncing back to an LTI/SSO portal) is honored consistently
// instead of each call site hardcoding its own /login fallback.
export function redirectAfterSignOut(
    logoutRedirectUrl: string | undefined,
    router: Router,
    fallback: RouteLocationRaw = '/login',
): void {
    if (logoutRedirectUrl) {
        window.location.replace(logoutRedirectUrl);
    } else {
        router.push(fallback);
    }
}

export async function signInWithAccessCode(accessCode: string): Promise<string> {
    const { email, otp, simulationID } = await EdgeFunctionsManager.getParticipantSignInData(accessCode);
    await supabaseClient.auth.verifyOtp({
        email,
        token: otp,
        type: 'magiclink',
    });
    return simulationID;
}

export async function getPublicUser(userId: string): Promise<UserRow | undefined> {
    const cached = publicUserCache.get(userId);
    if (cached) {
        return cached;
    }

    const pending = publicUserInflight.get(userId);
    if (pending) {
        return pending;
    }

    const promise = (async (): Promise<UserRow | undefined> => {
        const { data } = await supabaseClient.from('users').select('*').eq('id', userId).maybeSingle();
        if (isNullable(data)) {
            return undefined;
        }
        return camelcaseKeys(data) as unknown as UserRow;
    })();

    publicUserInflight.set(userId, promise);
    try {
        const result = await promise;
        publicUserCache.set(userId, result);
        return result;
    } finally {
        publicUserInflight.delete(userId);
    }
}

const publicUserCache = new Map<string, UserRow | undefined>();
const publicUserInflight = new Map<string, Promise<UserRow | undefined>>();
